import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { useOpenLayersContext } from '@ais-ui/react-openlayers';
import { Settings, useSaveSettings } from '@ais-ui/websockets';
import { useSettings } from '../../Settings/SettingsProvider';
import { useViewMode, ViewMode } from '../../../components/ViewModeProvider';

const TOAST_ID = 'zoom-buttons-suggestion';
const SESSION_KEY = 'zoomButtonsSuggestionHandled';

/**
 * Detect whether a wheel event came from a discrete mouse scroll wheel
 * rather than a trackpad / touchpad.
 *
 * - Firefox always reports `deltaMode === DOM_DELTA_LINE` (1) for mouse
 *   wheels and `DOM_DELTA_PIXEL` (0) for trackpads.
 * - Chromium / Safari use `DOM_DELTA_PIXEL` for both, but mouse wheels
 *   produce large, discrete jumps (≥ 100 px) while trackpads produce
 *   finer-grained deltas.
 */
function isMouseWheelEvent(e: WheelEvent): boolean {
  if (e.deltaMode === WheelEvent.DOM_DELTA_LINE) return true;

  return (
    e.deltaMode === WheelEvent.DOM_DELTA_PIXEL && Math.abs(e.deltaY) >= 100
  );
}

/**
 * Suggests enabling zoom buttons when a trackpad is detected.
 *
 * Instead of guessing based on timers, this hook observes actual wheel
 * events and inspects their properties to distinguish a real mouse
 * scroll wheel from a trackpad.
 *
 * - **Mouse wheel detected** → user has a scroll wheel, done.
 * - **Trackpad detected** → show toast suggesting zoom buttons.
 *
 * The check runs at most once per session (tracked via sessionStorage).
 *
 * Skipped entirely for touch-only devices (coarse pointer / no hover),
 * kiosk mode, or when zoom buttons are already enabled.
 */
export function useZoomButtonsSuggestion() {
  const { map } = useOpenLayersContext();
  const { settings } = useSettings();
  const { viewMode } = useViewMode();
  const { mutate: saveSettings } = useSaveSettings();
  const queryClient = useQueryClient();

  const handledRef = useRef(false);

  useEffect(() => {
    const isKiosk = viewMode === ViewMode.Kiosk;
    const zoomEnabled =
      settings['ribbonPlugin.settings']?.zoom_buttons_enabled ?? false;

    const isTouchOnly =
      window.matchMedia('(pointer: coarse)').matches &&
      window.matchMedia('(hover: none)').matches;

    if (
      isTouchOnly ||
      isKiosk ||
      zoomEnabled ||
      handledRef.current ||
      sessionStorage.getItem(SESSION_KEY)
    ) {
      return;
    }

    const viewport = map?.getViewport();
    if (!viewport) return;

    const markHandled = () => {
      handledRef.current = true;
      sessionStorage.setItem(SESSION_KEY, 'true');
    };

    const cleanup = () => {
      document.removeEventListener('wheel', onWheel);
    };

    const enableZoomButtons = () => {
      const freshSettings = queryClient.getQueryData<Settings>(['getSettings']);
      if (!freshSettings) return;

      saveSettings({
        ...freshSettings,
        'ribbonPlugin.settings': {
          ...freshSettings['ribbonPlugin.settings'],
          zoom_buttons_enabled: true,
        },
      });
    };

    const onWheel = (e: WheelEvent) => {
      if (isMouseWheelEvent(e)) {
        // Real mouse wheel — user has a scroll wheel, no toast needed.
        markHandled();
        cleanup();
        return;
      }

      // Trackpad detected — suggest zoom buttons.
      markHandled();
      cleanup();

      toast.info('Trackpad detected', {
        id: TOAST_ID,
        description: 'You can enable zoom buttons for easier map navigation.',
        duration: 20_000,
        action: {
          label: 'Enable',
          onClick: enableZoomButtons,
        },
        actionButtonStyle: {
          backgroundColor: 'hsl(var(--primary))',
          color: 'hsl(var(--primary-foreground))',
          border: 'none',
        },
      });
    };

    document.addEventListener('wheel', onWheel, { passive: true });

    return cleanup;
  }, [map, settings, viewMode, saveSettings, queryClient]);
}
